import { useId, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { Box, InlineError, Stack, Text } from '@/ui';
import { TextField } from '@/ui/primitives/TextField';

type AddOrganisationDialogProps = {
  open: boolean;
  saving?: boolean;
  name: string;
  errorMessage?: string | null;
  onNameChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
};

export const AddOrganisationDialog = ({
  open,
  saving = false,
  name,
  errorMessage,
  onNameChange,
  onClose,
  onSave,
}: AddOrganisationDialogProps): ReactElement => {
  const nameId = useId();
  const isSaveDisabled = name.trim().length === 0;
  const spacingMd = s('md');
  const spacingSm = s('sm');
  const Field = TextField;
  const fieldSx = {
    '& .MuiInputBase-root': {
      backgroundColor: 'background.default',
      minHeight: `${componentSizes.auth.inputHeight}px !important`,
      height: `${componentSizes.auth.inputHeight}px !important`,
      maxHeight: `${componentSizes.auth.inputHeight}px !important`,
      boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-root': {
      minHeight: `${componentSizes.auth.inputHeight}px !important`,
      height: `${componentSizes.auth.inputHeight}px !important`,
    },
    '& .MuiOutlinedInput-input': {
      height: '100%',
      boxSizing: 'border-box',
      paddingTop: s('none'),
      paddingBottom: s('none'),
      paddingLeft: spacingMd,
      paddingRight: spacingMd,
    },
  } as const;

  return (
    <ConfirmDialog
      open={open}
      title="Add Organisation"
      confirmLabel="Add Organisation"
      confirmColor="primary"
      confirmDisabled={isSaveDisabled}
      loading={saving}
      onClose={onClose}
      onConfirm={onSave}
      description={
        <Stack gap="md">
          <Box>
            <Box sx={{ mb: spacingSm }}>
              <Text role="body" component="label" htmlFor={nameId}>
                Name
              </Text>
            </Box>
            <Field
              id={nameId}
              value={name}
              onChange={(event) => {
                onNameChange(event.target.value);
              }}
              required
              autoFocus
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </Box>
          {errorMessage ? <InlineError message={errorMessage} /> : null}
        </Stack>
      }
      maxWidth="xs"
    />
  );
};
